import { useState } from 'react';
import {
  List,
  Avatar,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Spin,
  Typography,
  Empty,
} from 'antd';
import {
  PhoneOutlined,
  CheckCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  useWisecureMutation,
  useInvalidateQueries,
} from '@wisecure/api-client/hooks';
import { useAuthStore } from '@/stores';
import { queryKeys } from '@/api/query-keys';

const { Text } = Typography;

interface PendingPhoneUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
  phone: {
    countryCode: string | null;
    number: string | null;
    fullNumber: string | null;
  } | null;
  phoneVerified: string;
  createdAt: string;
}

const roleLabels: Record<string, string> = {
  CLIENT: 'Client',
  AGENT: 'Agent',
  SOCIETY_OWNER: 'Propriétaire',
  ADMIN: 'Admin',
};

const roleColors: Record<string, string> = {
  CLIENT: 'blue',
  AGENT: 'green',
  SOCIETY_OWNER: 'purple',
  ADMIN: 'red',
};

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const baseUrl = import.meta.env.VITE_API_URL ?? '';

export function PhoneValidationList() {
  const invalidate = useInvalidateQueries();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PendingPhoneUser | null>(null);
  const [form] = Form.useForm();

  const { data: users, isLoading } = useQuery<PendingPhoneUser[]>({
    queryKey: queryKeys.dashboardPendingPhoneValidations,
    queryFn: async () => {
      const resp = await fetch(
        `${baseUrl}/api/dashboard/pending-phone-validations`,
        { headers: authHeaders() },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
  });

  const validateMutation = useWisecureMutation(
    async (_client, userId: string) => {
      const resp = await fetch(
        `${baseUrl}/api/dashboard/users/${userId}/validate-phone`,
        {
          method: 'POST',
          headers: authHeaders(),
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    {
      onSuccess: () => {
        message.success('Téléphone validé');
        invalidate(queryKeys.dashboardPendingPhoneValidations);
      },
      onError: () => message.error('Erreur lors de la validation'),
    },
  );

  const updatePhoneMutation = useWisecureMutation(
    async (
      _client,
      { userId, countryCode, number }: { userId: string; countryCode: string; number: string },
    ) => {
      const resp = await fetch(
        `${baseUrl}/api/dashboard/users/${userId}/phone`,
        {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ countryCode, number }),
        },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.message ?? `HTTP ${resp.status}`);
      }
      return resp.json();
    },
    {
      onSuccess: () => {
        message.success('Numéro de téléphone mis à jour');
        setEditModalOpen(false);
        setEditingUser(null);
        form.resetFields();
        invalidate(queryKeys.dashboardPendingPhoneValidations);
      },
      onError: (err) =>
        message.error(
          err instanceof Error ? err.message : 'Erreur lors de la mise à jour',
        ),
    },
  );

  const handleValidate = (userId: string) => {
    validateMutation.mutate(userId);
  };

  const handleEdit = (user: PendingPhoneUser) => {
    setEditingUser(user);
    form.setFieldsValue({
      countryCode: user.phone?.countryCode ?? 'FR',
      number: user.phone?.number ?? '',
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    form.validateFields().then((values) => {
      if (!editingUser) return;
      updatePhoneMutation.mutate({
        userId: editingUser.id,
        countryCode: values.countryCode,
        number: values.number,
      });
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Empty
        description="Aucun numéro de téléphone en attente de validation"
        style={{ padding: 40 }}
      />
    );
  }

  return (
    <>
      <List
        dataSource={users}
        renderItem={(item: PendingPhoneUser) => (
          <List.Item
            actions={[
              <Button
                key="validate"
                type="primary"
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={() => handleValidate(item.id)}
                loading={validateMutation.isPending}
              >
                Valider
              </Button>,
              <Button
                key="edit"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(item)}
              >
                Modifier
              </Button>,
            ]}
          >
            <List.Item.Meta
              avatar={<Avatar icon={<PhoneOutlined />} />}
              title={
                <Space>
                  <span>
                    {item.firstName ?? ''} {item.lastName ?? ''}
                  </span>
                  <Tag color={roleColors[item.role] ?? 'default'}>
                    {roleLabels[item.role] ?? item.role}
                  </Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size={2}>
                  <Text type="secondary">{item.email}</Text>
                  {item.phone && (
                    <Text>
                      {item.phone.fullNumber ??
                        `${item.phone.countryCode ?? ''} ${item.phone.number ?? ''}`}
                    </Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />

      <Modal
        title="Modifier le numéro de téléphone"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingUser(null);
          form.resetFields();
        }}
        confirmLoading={updatePhoneMutation.isPending}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="countryCode"
            label="Code pays"
            rules={[{ required: true, message: 'Code pays requis' }]}
          >
            <Input placeholder="FR" />
          </Form.Item>
          <Form.Item
            name="number"
            label="Numéro de téléphone"
            rules={[{ required: true, message: 'Numéro requis' }]}
          >
            <Input placeholder="612345678" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
